import { useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import DOMPurify from 'dompurify';
import posthog from '@leyan/lytics';
import copyText from 'utils/copyText';
import dayjs from 'lib/dayjs';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import useIM from 'hooks/business/im/useIM';
import useTradeTranslate, {
  LogisticsPackage,
  PlatformLogistic,
  getTranslation,
} from './useTradeTranslate';

export type LogisticsPlatform = 'SHOPEE' | 'LAZADA';

export interface UseLogisticsListBusinessParams<T extends LogisticsPlatform> {
  conversation?: Conversation.Conversation;
  logisticsPackageList?: LogisticsPackage<T>[];
  fold?: boolean;
  exportMode?: boolean;
  isThird?: boolean;
  reportTranslation?: boolean;
  isReverse?: boolean;
}

export interface UseLogisticsListBusinessResult {
  conversation?: Conversation.Conversation;
  needTranslate: boolean;
  displayTranslation: boolean;
  translating: boolean;
  translationError: Error | null;
  foldCtl: boolean;
  isTikTok: boolean;
  setFoldCtl: Dispatch<SetStateAction<boolean>>;
  onToggleTranslation: () => void;
  onRetranslate: () => void;
  onCopyAll: () => void;
  onSendTiktokLogisticsCard: (params: { orderId?: string; packageId?: string }) => void;
}

export const isTrackingInfoValid = (
  trackingInfo: Conversation.Trade.BaseTrackingInfo[],
  lang?: General.SystemLanguage,
) => {
  return trackingInfo.every((info) => {
    const translation = getTranslation(info.descriptionTranslation, lang);
    return typeof translation === 'string';
  });
};

const isTranslationValid = (
  logisticsPackageList?: LogisticsPackage[],
  lang?: General.SystemLanguage,
) => {
  return logisticsPackageList?.every((logisticsPackage: any) => {
    const trackingInfo = logisticsPackage?.trackingInfo || [];

    return isTrackingInfoValid(trackingInfo, lang);
  });
};

export const useLogisticsListBusiness = <T extends LogisticsPlatform>(
  params: UseLogisticsListBusinessParams<T>,
): UseLogisticsListBusinessResult => {
  const {
    logisticsPackageList,
    fold = false,
    exportMode = false,
    isThird = false,
    reportTranslation,
    conversation,
    isReverse = false,
  } = params;

  const { t } = useTranslate();
  const systemLang = useSystemLanguageValue();
  const sendTiktokLogisticsCard = useSendTiktokLogisticsCard();
  const needTranslate = !exportMode;

  const [displayTranslation, setDisplayTranslation] = useState(
    () => isTranslationValid(logisticsPackageList, systemLang) && needTranslate,
  );
  const [foldCtl, setFoldCtl] = useState(fold);

  const {
    pending: translating,
    error: translationError,
    translateTradeLogistics,
    translateLazadaLogistics,
  } = useTradeTranslate(reportTranslation);

  const copyContent = useMemo(() => {
    const list = logisticsPackageList ? [...logisticsPackageList] : [];
    return list
      .map((logistics) => {
        const { trackingNumber, trackingInfo } = logistics;

        return [
          t('domain.order.logistics.field.trackingNumber'),
          trackingNumber,
          trackingInfo
            .map((info) => {
              const { updateTime, description, descriptionTranslation } = info;

              const validUpdateTime = Number(updateTime);
              const time = validUpdateTime
                ? dayjs(validUpdateTime * 1000).format('YYYY-MM-DD HH:mm')
                : '';
              const desc = displayTranslation
                ? getTranslation(descriptionTranslation)
                : description;
              let text = desc;

              try {
                text = DOMPurify.sanitize(decodeURIComponent(desc), { RETURN_DOM: true }).innerText;
              } catch (error) {
                // ignore malformed URI payload and fallback to the original description
              }

              return time ? `${text}\n${time}\n` : `${text}\n`;
            })
            .join('\n'),
        ].join('\n');
      })
      .join('\n\n');
  }, [displayTranslation, logisticsPackageList, t]);

  const onToggleTranslation = useCallback(() => {
    if (!conversation) {
      return;
    }

    if (displayTranslation) {
      setDisplayTranslation(false);

      return;
    }

    if (isTranslationValid(logisticsPackageList, systemLang)) {
      setDisplayTranslation(true);

      return;
    }

    const { channel } = conversation;

    if (
      isThird ||
      isReverse ||
      channel === 'SHOPEE' ||
      isTiKTokUpperCase(channel) ||
      channel === 'ALIEXPRESS' ||
      channel === 'KWAI'
    ) {
      translateTradeLogistics(
        channel as Exclude<PlatformLogistic, 'LAZADA'>,
        logisticsPackageList as LogisticsPackage<'SHOPEE'>[],
        isThird,
        isReverse,
      )
        .then(() => {
          setDisplayTranslation(true);
        })
        .catch(() => {});

      return;
    }

    if (!isThird && channel === 'LAZADA') {
      translateLazadaLogistics(logisticsPackageList as LogisticsPackage<'LAZADA'>[])
        .then(() => {
          setDisplayTranslation(true);
        })
        .catch(() => {});
    }
  }, [
    conversation,
    displayTranslation,
    isReverse,
    isThird,
    logisticsPackageList,
    systemLang,
    translateLazadaLogistics,
    translateTradeLogistics,
  ]);

  const onRetranslate = useCallback(() => {
    if (!conversation) {
      return;
    }

    const { channel } = conversation;

    if (channel === 'SHOPEE' || isTiKTokUpperCase(channel) || channel === 'ALIEXPRESS') {
      translateTradeLogistics(
        channel,
        logisticsPackageList as LogisticsPackage<'SHOPEE'>[],
        isThird,
        isReverse,
      )
        .then(() => {
          setDisplayTranslation(true);
        })
        .catch(() => {});

      return;
    }

    if (channel === 'LAZADA') {
      translateLazadaLogistics(logisticsPackageList as LogisticsPackage<'LAZADA'>[])
        .then(() => {
          setDisplayTranslation(true);
        })
        .catch(() => {});
    }
  }, [
    conversation,
    isReverse,
    isThird,
    logisticsPackageList,
    translateLazadaLogistics,
    translateTradeLogistics,
  ]);

  const refsCurrent = {
    logisticsPackageList,
    onToggleTranslation,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { logisticsPackageList, onToggleTranslation },
    } = refs;

    if (!needTranslate) {
      setDisplayTranslation(false);

      return;
    }

    if (isTranslationValid(logisticsPackageList, systemLang)) {
      return;
    }

    setDisplayTranslation(false);
    onToggleTranslation();
  }, [needTranslate, systemLang]);

  const onCopyAll = useCallback(() => {
    copyText(copyContent)
      .then(() => {
        message.success(t('common.feedback.copySuccess'));
      })
      .catch(() => {
        message.error(t('common.feedback.copyFailed'));
      });

    posthog.capture('$tradeCopyAll');
  }, [copyContent, t]);

  const onSendTiktokLogisticsCard = useCallback(
    (params: { orderId?: string; packageId?: string }) => {
      const { orderId, packageId } = params;

      if (!conversation || !orderId || !packageId) {
        return;
      }
      sendTiktokLogisticsCard({ orderId, packageId, conversation });
    },
    [conversation, sendTiktokLogisticsCard],
  );

  return {
    needTranslate,
    displayTranslation: displayTranslation || false,
    translating,
    translationError,
    foldCtl,
    isTikTok: isTiKTokUpperCase(conversation?.channel),
    setFoldCtl,
    onToggleTranslation,
    onRetranslate,
    onCopyAll,
    onSendTiktokLogisticsCard,
  };
};

export const useSendTiktokLogisticsCard = () => {
  const { sendTtsLogisticsCard } = useIM();

  const sendTiktokLogisticsCard = useCallback(
    (params: {
      orderId?: string;
      packageId?: string;
      conversation?: Conversation.Conversation;
    }) => {
      const { orderId, packageId, conversation } = params;

      if (!conversation || !orderId || !packageId) {
        return;
      }

      sendTtsLogisticsCard(conversation.conversationId, {
        orderId,
        packageId,
      });
    },
    [sendTtsLogisticsCard],
  );

  return sendTiktokLogisticsCard;
};
